import { DEFAULT_CATEGORY_BAND_IMAGE, CATEGORY_ICON_BY_KEY, imageOrDefault } from '../default-images';
import type { SectionRendererProps } from '../section-renderer.types';

interface CategoryItem {
  label: string;
  icon?: string;
}

interface CategoryBandContent {
  eyebrow?: string;
  title?: string;
  body?: string;
  image?: string;
  categories: CategoryItem[];
}

/**
 * Background-image band with icon+label category pills (Trusted Partners'
 * "Partner Categories", FR-146, `02-design.md` §22). `image` is optional in
 * the schema but always has a bundled Frontend default for this section's
 * key - see `default-images.ts`'s `DEFAULT_CATEGORY_BAND_IMAGE` and the
 * schema's own correction note for why.
 */
export function CategoryBandRenderer({ section }: SectionRendererProps) {
  const c = section.content as unknown as CategoryBandContent;
  const backgroundImage = imageOrDefault(c.image, DEFAULT_CATEGORY_BAND_IMAGE[section.key]);

  return (
    <section className="relative w-full overflow-hidden bg-navy">
      {backgroundImage && (
        <img src={backgroundImage} alt="" className="absolute inset-0 size-full object-cover" />
      )}
      <div className="from-navy to-navy/0 absolute inset-0 bg-gradient-to-r" />
      <div className="relative mx-auto flex w-full max-w-328 flex-col gap-8 px-4 py-24">
        <div className="flex max-w-3xl flex-col gap-2">
          {c.eyebrow && <p className="text-p2 font-semibold text-white uppercase">{c.eyebrow}</p>}
          {c.title && <h2 className="text-h2 text-white">{c.title}</h2>}
          {c.body && <p className="text-p3 text-white">{c.body}</p>}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {c.categories?.map((category, i) => {
            const icon = category.icon ? CATEGORY_ICON_BY_KEY[category.icon] : undefined;
            return (
              <div
                key={i}
                className="flex items-center gap-4 rounded-3xl border border-white/25 bg-navy/70 p-4 shadow-[0_6px_18px_0_rgba(0,0,0,0.05)]"
              >
                {icon && (
                  <div className="bg-dkgreen/10 flex size-[50px] shrink-0 items-center justify-center rounded-2xl">
                    <img src={icon} alt="" className="size-8" />
                  </div>
                )}
                <p className="text-p2 font-semibold text-white">{category.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
